import {
  Stack,
  LocalizedString,
  Loader,
  ErrorBanner,
  ContentRow,
  Box,
  useIsMobileLayout,
  useForm,
  Button,
  LoadingButton,
} from "@dbn/omnichannel-design-system-g4p";
import { boolean, option, taskEither } from "fp-ts";
import { IO } from "fp-ts/IO";
import { pipe, constVoid, constNull } from "fp-ts/function";
import { RuntimeLocaleKey, useFormatMessage } from "../../intl";
import { BackButton } from "../../Common/BackButton/BackButton";
import { PageHeading } from "../../Common/PageHeading/PageHeading";
import { OfferReviewCard } from "./OfferReviewCard";
import { useCommand, useQuery } from "../../useAPI";
import * as remoteData from "../../RemoteData";
import * as api from "../api";
import { Option } from "fp-ts/Option";
import { ExistingClientAuthenticationMethod } from "../../globalDomain";
import { useValidators } from "../../Common/useValidators";
import { DisbursementAccountsField } from "../DisbursementAccountsField";
import { OfferDocuments } from "./OfferDocuments";
import { useState } from "react";
import OfferReviewSelect3P from "./OfferReviewSelect3P";
import { useAppContext } from "../../useAppContext";
import { useIs3PChannel, useIsInPersonChannel } from "../../useChannel";
import { RejectionReason } from "../StandardLoanState";

export type OnApplicationRejected = (result: RejectionReason) => void;

type Props = {
  onBack: IO<unknown>;
  onComplete: () => unknown;
  onApplicationRejected: OnApplicationRejected;
  selectedOffer: api.GenericLoanResponseOutput;
  authenticationMethod: Option<ExistingClientAuthenticationMethod>;
  existingClient: boolean;
};

export function OfferReview(props: Props) {
  const formatMessage = useFormatMessage();

  const [appStateRequiredDocuments] = useQuery(
    api.appStateRequiredDocumentsReviewOffer
  );
  const isMobileLayout = useIsMobileLayout();

  const setDisbursementAccount = useCommand(api.setDisbursementAccount);

  const { definedNoExtract } = useValidators();

  const { fieldProps, handleSubmit } = useForm(
    {
      initialValues: {
        disbursementAccount: option.none as Option<LocalizedString>,
      },
      fieldValidators: () =>
        props.existingClient
          ? {
              disbursementAccount: definedNoExtract<LocalizedString>(),
            }
          : {},
    },
    {
      onSubmit: data =>
        pipe(
          data.disbursementAccount,
          option.fold(
            () => taskEither.fromIO(constVoid),
            disbursementAccount => setDisbursementAccount(disbursementAccount)
          ),
          taskEither.chain(() => taskEither.fromIO(props.onComplete))
        ),
    }
  );

  const continueButton = pipe(
    props.authenticationMethod,
    option.fold(
      () => (
        <Button
          label={formatMessage("StandardLoan.OfferApproved.OfferReview.accept")}
          variant="primary"
          size="default"
          action={props.onComplete}
        />
      ),
      () => (
        <LoadingButton
          variant="primary"
          type="submit"
          labels={{
            normal: formatMessage(
              "StandardLoan.OfferApproved.OfferReview.accept"
            ),
            loading: formatMessage(
              "StandardLoan.OfferApproved.OfferReview.accept"
            ),
            error: formatMessage(
              "StandardLoan.OfferApproved.OfferReview.accept"
            ),
            success: formatMessage(
              "StandardLoan.OfferApproved.OfferReview.accept"
            ),
          }}
          size="default"
          action={handleSubmit}
        />
      )
    )
  );
  const [accountsLoading, setAccountsLoading] = useState(props.existingClient);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const handleAccountsFinishLoading = () => {
    setAccountsLoading(false);
  };
  const handleDocumentsSuccess = () => {
    if (documentsLoading) {
      setDocumentsLoading(false);
    }
    return <OfferDocuments />;
  };
  const handleDocumentsFailure = () => {
    if (documentsLoading) {
      setDocumentsLoading(false);
    }
    return (
      <ErrorBanner>
        {formatMessage("StandardLoan.UploadDocuments.loadingError")}
      </ErrorBanner>
    );
  };
  const {
    config: { enableCashLoanId3PInBranch },
  } = useAppContext();
  const isInPersonChannel = useIsInPersonChannel();
  const is3PChannel = useIs3PChannel();

  return (
    <Stack units={10} column grow shrink>
      <PageHeading
        title={formatMessage("StandardLoan.OfferApproved.OfferReview.title")}
        contentRowType={"full"}
      />
      <ContentRow type="full">
        <Box column shrink grow>
          <Stack column units={6}>
            <OfferReviewCard offer={props.selectedOffer} />
            {props.existingClient && (
              <DisbursementAccountsField
                onFinishLoading={handleAccountsFinishLoading}
                {...fieldProps("disbursementAccount")}
              />
            )}
            {pipe(
              appStateRequiredDocuments,
              remoteData.fold(
                constNull,
                handleDocumentsFailure,
                handleDocumentsSuccess
              )
            )}
            {(accountsLoading || documentsLoading) && (
              <Box hAlignContent="center">
                <Loader
                  ariaLabelMessage={formatMessage(
                    "generic.loading.alt" as RuntimeLocaleKey
                  )}
                />
              </Box>
            )}
            {enableCashLoanId3PInBranch &&
              isInPersonChannel &&
              !is3PChannel && <OfferReviewSelect3P />}
          </Stack>
        </Box>
      </ContentRow>

      <ContentRow type="full">
        <Box column shrink grow>
          {pipe(
            isMobileLayout,
            boolean.fold(
              () => (
                <Stack>
                  <BackButton action={props.onBack} />
                  {continueButton}
                </Stack>
              ),
              () => (
                <Stack column units={6}>
                  {continueButton}
                  <BackButton action={props.onBack} />
                </Stack>
              )
            )
          )}
        </Box>
      </ContentRow>
    </Stack>
  );
}
